import { useState, useEffect, useRef, useCallback } from 'react';
import default_bg from '../../img/carousel_img.jpeg';
import carousel_bg_1 from '../../img/group_pic.jpeg';
import carousel_bg_3_mobile from '../../img/group_pic.jpeg';
import LoadingOverlay from '../uiComponents/loading';
import '../../css/main.css';

// Small debounce helper
const debounce = (fn, wait = 120) => {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
    };
};

const Carousel = () => {
    const [images, setImages] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    const currentRef = useRef(null);
    const nextRef = useRef(null);
    const containerRef = useRef(null);
    const isSlidingRef = useRef(false);
    const visibleRef = useRef(true);

    // Choose images by viewport size
    const chooseImages = useCallback(() => {
        if (window.innerWidth > 768) setImages([default_bg, carousel_bg_1]);
        else setImages([default_bg, carousel_bg_3_mobile]);
    }, []);

    useEffect(() => {
        chooseImages();
        const onResize = debounce(() => {
            chooseImages();
            // Reset transforms to avoid stale positions
            if (currentRef.current && nextRef.current) {
                currentRef.current.style.transition = 'none';
                nextRef.current.style.transition = 'none';
                currentRef.current.style.transform = 'translateX(0)';
                nextRef.current.style.transform = 'translateX(100%)';
            }
        }, 150);

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [chooseImages]);

    // Preload next image when currentIndex or images change
    useEffect(() => {
        if (!images || images.length === 0) return;
        const nextIndex = (currentIndex + 1) % images.length;
        const img = new Image();
        img.src = images[nextIndex];
    }, [currentIndex, images]);

    // IntersectionObserver to pause when not visible
    useEffect(() => {
        const el = containerRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;
        const obs = new IntersectionObserver(([entry]) => {
            visibleRef.current = entry.isIntersecting;
        }, { threshold: 0.4 });
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    const move = useCallback((dir = 'forward') => {
        if (isSlidingRef.current || images.length < 2) return;
        isSlidingRef.current = true;

        const cur = currentRef.current;
        const nxt = nextRef.current;
        if (!cur || !nxt) {
            isSlidingRef.current = false;
            return;
        }

        if (dir === 'forward') {
            // ensure next has correct src
            nxt.src = images[(currentIndex + 1) % images.length];
            nxt.style.transition = 'none';
            nxt.style.transform = 'translateX(100%)';
            // force reflow
            void nxt.offsetWidth;

            cur.style.transition = 'transform 0.8s ease';
            nxt.style.transition = 'transform 0.8s ease';

            cur.style.transform = 'translateX(-100%)';
            nxt.style.transform = 'translateX(0)';

            setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % images.length);
                // reset without transition
                cur.style.transition = 'none';
                nxt.style.transition = 'none';
                cur.style.transform = 'translateX(0)';
                nxt.style.transform = 'translateX(100%)';
                isSlidingRef.current = false;
            }, 820);
        } else {
            // backward
            const prevIndex = (currentIndex - 1 + images.length) % images.length;
            nxt.src = images[prevIndex];
            nxt.style.transition = 'none';
            nxt.style.transform = 'translateX(-100%)';
            void nxt.offsetWidth;

            cur.style.transition = 'transform 0.8s ease';
            nxt.style.transition = 'transform 0.8s ease';

            cur.style.transform = 'translateX(100%)';
            nxt.style.transform = 'translateX(0)';

            setTimeout(() => {
                setCurrentIndex(prevIndex);
                // reset
                cur.style.transition = 'none';
                nxt.style.transition = 'none';
                cur.style.transform = 'translateX(0)';
                nxt.style.transform = 'translateX(100%)';
                isSlidingRef.current = false;
            }, 820);
        }
    }, [images, currentIndex]);

    // Auto cycle (pauses when not visible)
    useEffect(() => {
        const id = setInterval(() => {
            if (visibleRef.current) move('forward');
        }, 3500);
        return () => clearInterval(id);
    }, [move]);

    const handleImageLoad = () => {
        setIsLoading(false);
    };

    if (!images || images.length === 0) return null;

    return (
        <section
            ref={containerRef}
            className="banner-area"
            id="home"
            style={{ overflow: 'hidden', position: 'relative' }}
            onMouseEnter={() => { visibleRef.current = false; }}
            onMouseLeave={() => { visibleRef.current = true; }}
            onFocus={() => { visibleRef.current = false; }}
            onBlur={() => { visibleRef.current = true; }}
            tabIndex={-1}
            aria-label="Homepage carousel"
        >
            {isLoading && <LoadingOverlay />}

            <div className="carousel-arrows">
                <i className="fa fa-chevron-left back" onClick={() => move('backward')}></i>
                <i className="fa fa-chevron-right front" onClick={() => move('forward')}></i>

            </div>


            <img
                ref={currentRef}
                className="header-img active"
                src={images[currentIndex]}
                alt="Current Background"
                loading="lazy"
                onLoad={handleImageLoad}
                style={{
                    zIndex: 1,
                    transform: 'translateX(0)',
                    filter: 'brightness(50%)',
                }}
                draggable="false"
            />

            <img
                ref={nextRef}
                className="header-img"
                src={images[(currentIndex + 1) % images.length]}
                alt="Next Background"
                loading="lazy"
                onLoad={() => {/* noop: preloaded */ }}
                style={{
                    zIndex: 2,
                    transform: 'translateX(100%)',
                    filter: 'brightness(50%)',
                }}
                draggable="false"
            />

            {!isLoading && (
                <div className="container">
                    <div className="row fullscreen d-flex align-items-center justify-content-start">
                        <div className="banner-content col-lg-7">
                            <h6 className="text-white text-uppercase">Serve the Lord with gladness! - Psalm 100:2</h6>
                            <h1>Baptist Church Alkapoor <br /></h1>
                            <a href="#about" className="primary-btn text-uppercase">About</a>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default Carousel;